import {
  InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useQuery,
} from '@tanstack/react-query';
import { api, OrganizationSummaryWithCountsListDto } from '@sib/api';
import { DEFAULT_PAGE, MAXIMUM_PAGINATION } from '@sib/api/constants';
import { QueryKey } from '@sib/api/enums';

export const useGetMembers = (
  organizationId?: string,
  query?: Parameters<
    typeof api.member.memberControllerFindUsersWithTaskByOrganization
  >[1]
) =>
  useQuery({
    enabled: !!organizationId,
    queryKey: [
      QueryKey.Resource.OrganizationMembers,
      organizationId,
      QueryKey.Operation.List,
      query,
      QueryKey.Operation.Infinite,
    ],
    queryFn: async () =>
      api.member.memberControllerFindUsersWithTaskByOrganization(
        organizationId || '',
        query || {
          page: DEFAULT_PAGE,
          size: MAXIMUM_PAGINATION,
        }
      ),
  });

export const useGetInfiniteOrganizationSummary = (
  query: Parameters<
    typeof api.member.memberControllerGetOrganizationMembersSummary
  >[0],
  options?: Partial<
    UseInfiniteQueryOptions<
      OrganizationSummaryWithCountsListDto,
      Error,
      InfiniteData<OrganizationSummaryWithCountsListDto>
    >
  >
) =>
  useInfiniteQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.OrganizationSummary,
      QueryKey.Operation.List,
      query,
      QueryKey.Operation.Infinite,
    ],
    queryFn: ({ pageParam }) =>
      api.member.memberControllerGetOrganizationMembersSummary({
        ...query,
        page: pageParam as number,
      }),
    getNextPageParam: lastPage => {
      const { hasNextPage } = lastPage;
      const nextPage = lastPage.page + 1;

      return hasNextPage ? nextPage : undefined;
    },
    initialPageParam: query?.page || DEFAULT_PAGE,
    placeholderData: keepPreviousData,
  });

export const useGetOrganizationSummaryActiveTasks = (
  query?: Parameters<
    typeof api.member.memberControllerGetOrganizationMembersSummaryByActiveTasks
  >[0]
) =>
  useQuery({
    queryKey: [
      QueryKey.Resource.OrganizationActiveTasks,
      QueryKey.Operation.List,
      query,
    ],
    queryFn: async () =>
      api.member.memberControllerGetOrganizationMembersSummaryByActiveTasks(
        query || {
          page: DEFAULT_PAGE,
          size: MAXIMUM_PAGINATION,
        }
      ),
  });

export const useGetOrganizationSummaryActionItems = (
  query?: Parameters<
    typeof api.member.memberControllerGetOrganizationMembersSummaryActionItems
  >[0]
) =>
  useQuery({
    queryKey: [
      QueryKey.Resource.OrganizationSummaryActionItems,
      QueryKey.Operation.List,
      query,
    ],
    queryFn: async () =>
      api.member.memberControllerGetOrganizationMembersSummaryActionItems(
        query || {
          page: DEFAULT_PAGE,
          size: MAXIMUM_PAGINATION,
        }
      ),
  });
